import { motion } from "framer-motion";
import { useState } from "react";
import { ArrowUpRight, Cpu, HardDrive, MemoryStick, Zap, CircuitBoard, Network, Wrench } from "lucide-react";
import { SectionHeader } from "@/components/GlobalTradingNetwork";
import QuoteDialog from "@/components/QuoteDialog";
import { easeExpo } from "@/lib/motion";

const IMG = {
  cpu: "https://images.pexels.com/photos/11272008/pexels-photo-11272008.jpeg?auto=compress&cs=tinysrgb&w=1400",
  ic: "https://images.pexels.com/photos/8108726/pexels-photo-8108726.jpeg?auto=compress&cs=tinysrgb&w=1400",
  net: "https://images.pexels.com/photos/37730211/pexels-photo-37730211.jpeg?auto=compress&cs=tinysrgb&w=1400",
  ssd: "https://images.pexels.com/photos/11216304/pexels-photo-11216304.jpeg?auto=compress&cs=tinysrgb&w=1400",
  memory: "https://images.pexels.com/photos/6636474/pexels-photo-6636474.jpeg?auto=compress&cs=tinysrgb&w=1400",
  gpu: "https://images.pexels.com/photos/6704945/pexels-photo-6704945.jpeg?auto=compress&cs=tinysrgb&w=1400",
};

const CATEGORIES = [
  {
    key: "cpu",
    label: "CPU",
    tagline: "Server & desktop processors",
    parts: ["Xeon", "EPYC", "Ryzen", "Core i9"],
    image: IMG.cpu,
    icon: Cpu,
    span: "md:col-span-7 md:row-span-2",
    kind: "photo",
  },
  {
    key: "ssd",
    label: "SSD",
    tagline: "NVMe & enterprise storage",
    parts: ["U.2", "M.2", "PCIe Gen5", "SATA"],
    image: IMG.ssd,
    icon: HardDrive,
    span: "md:col-span-5",
    kind: "photo",
  },
  {
    key: "memory",
    label: "Memory",
    tagline: "DRAM · DDR4 · DDR5 · HBM",
    parts: ["RDIMM", "LRDIMM", "SODIMM", "HBM3"],
    image: IMG.memory,
    icon: MemoryStick,
    span: "md:col-span-5",
    kind: "photo",
  },
  {
    key: "ic",
    label: "IC",
    tagline: "Integrated circuits & MCUs",
    parts: ["FPGA", "ASIC", "MCU", "Analog"],
    image: IMG.ic,
    icon: CircuitBoard,
    span: "md:col-span-7 md:row-span-2",
    kind: "photo",
  },
  {
    key: "gpu",
    label: "GPU",
    tagline: "AI accelerators & discrete cards",
    parts: ["H100", "A100", "L40S", "RTX"],
    image: IMG.gpu,
    icon: Zap,
    span: "md:col-span-5",
    kind: "photo",
  },
  {
    key: "networking",
    label: "Networking",
    tagline: "Switches, NICs, transceivers",
    parts: ["400G", "InfiniBand", "SFP+", "QSFP-DD"],
    image: IMG.net,
    icon: Network,
    span: "md:col-span-7",
    kind: "photo",
  },
  {
    key: "industrial",
    label: "Industrial Components",
    tagline: "Ruggedized, long-lifecycle parts",
    parts: ["Automotive", "Aerospace", "MIL-spec"],
    icon: Wrench,
    span: "md:col-span-5",
    kind: "abstract",
    accent: "from-indigo-500/25 to-transparent",
  },
];

export default function ProductCategories() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [defaultCategory, setDefaultCategory] = useState("");

  const openQuote = (cat) => {
    setDefaultCategory(cat);
    setQuoteOpen(true);
  };

  return (
    <section
      id="products"
      className="relative py-28 md:py-40"
      data-testid="section-products"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <SectionHeader
          index="02"
          overline="Product Categories"
          title={
            <>
              Everything the <br />
              rack <span className="text-electric-500">needs</span>.
            </>
          }
          copy="Seven verticals of critical enterprise silicon — from the sockets that power AI clusters to the ruggedized ICs that keep factories running. All sourced from authorized channels, tested, and shipped worldwide."
        />

        <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-4 auto-rows-[180px] md:auto-rows-[220px]">
          {CATEGORIES.map((c, i) => (
            <motion.button
              key={c.key}
              onClick={() => openQuote(c.label)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.9, ease: easeExpo, delay: (i % 4) * 0.05 }}
              data-testid={`product-card-${c.key}`}
              className={`group on-dark relative overflow-hidden rounded-2xl text-left border border-white/8 bg-ink-900/60 col-span-1 ${c.span} focus:outline-none focus:ring-2 focus:ring-electric-500/50`}
              style={{ minHeight: 220 }}
            >
              {/* Photo cards */}
              {c.kind === "photo" ? (
                <>
                  <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                      backgroundImage: `url(${c.image})`,
                      transform: "scale(1.02)",
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-ink-950 via-ink-900/70 to-ink-900/10" />
                  <div className="absolute inset-0"
                    style={{
                      background:
                        "radial-gradient(circle at 85% 15%, rgba(0,229,255,0.18), transparent 55%)",
                    }}
                  />
                </>
              ) : (
                <>
                  <div className={`absolute inset-0 bg-gradient-to-br ${c.accent}`} />
                  <div className="absolute inset-0 bg-grid-fine opacity-40" />
                  <AbstractDeco kind={c.key} />
                </>
              )}

              {/* Content */}
              <div className="relative h-full flex flex-col justify-between p-6 md:p-7">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5 text-[10px] font-mono tracking-[0.22em] uppercase text-slate-300">
                    <c.icon className="w-3.5 h-3.5 text-electric-500" />
                    Category · {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <ArrowUpRight className="w-5 h-5 text-electric-500" />
                  </div>
                </div>

                <div>
                  <div className="font-display text-3xl md:text-4xl tracking-tight text-white">
                    {c.label}
                  </div>
                  <div className="mt-1.5 text-slate-300 text-sm">{c.tagline}</div>

                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {c.parts.map((p) => (
                      <span
                        key={p}
                        className="text-[10px] font-mono tracking-wider uppercase px-2 py-0.5 rounded-full border border-white/15 text-slate-200/90 bg-black/20 backdrop-blur-sm"
                      >
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Hover reveal border */}
              <div
                className="absolute inset-0 rounded-2xl border border-electric-500/0 group-hover:border-electric-500/40 pointer-events-none"
                style={{ transition: "border-color 500ms cubic-bezier(0.16,1,0.3,1)" }}
              />
            </motion.button>
          ))}
        </div>

        <div className="mt-10 flex items-center justify-between flex-wrap gap-4">
          <p className="text-slate-400 text-sm max-w-lg">
            Can&apos;t find a part number? Send us your BOM — we specialize in EOL,
            allocated, and hard-to-find components.
          </p>
          <button
            data-testid="products-bom-quote-button"
            onClick={() => openQuote("Other")}
            className="btn-ghost rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2"
          >
            Send us a BOM <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <QuoteDialog
        open={quoteOpen}
        onOpenChange={setQuoteOpen}
        defaultCategory={defaultCategory}
      />
    </section>
  );
}

function AbstractDeco({ kind }) {
  if (kind === "ssd") {
    return (
      <svg
        viewBox="0 0 400 260"
        className="absolute right-2 bottom-0 w-2/3 opacity-50"
        preserveAspectRatio="xMaxYMax slice"
      >
        <defs>
          <linearGradient id="ssdG" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0" stopColor="#00E5FF" stopOpacity="0.6" />
            <stop offset="1" stopColor="#00E5FF" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[...Array(24)].map((_, i) => (
          <rect
            key={i}
            x={20 + i * 14}
            y={60 + Math.sin(i * 0.6) * 20}
            width="6"
            height={80 + Math.cos(i * 0.6) * 40}
            fill="url(#ssdG)"
          />
        ))}
      </svg>
    );
  }
  if (kind === "memory") {
    return (
      <svg
        viewBox="0 0 400 260"
        className="absolute right-0 bottom-0 w-2/3 opacity-45"
      >
        <g stroke="#7DD3FC" strokeOpacity="0.35" fill="none" strokeWidth="1">
          {[...Array(16)].map((_, i) => (
            <rect
              key={i}
              x={30 + i * 22}
              y={80}
              width="14"
              height="120"
              rx="2"
            />
          ))}
          <rect x={20} y={70} width={360} height={140} rx="6" strokeWidth="1.2" />
        </g>
      </svg>
    );
  }
  if (kind === "gpu") {
    return (
      <svg
        viewBox="0 0 400 260"
        className="absolute inset-x-0 bottom-0 w-full opacity-40"
      >
        <defs>
          <linearGradient id="gpuG" x1="0" x2="1">
            <stop offset="0" stopColor="#00E5FF" stopOpacity="0" />
            <stop offset="0.5" stopColor="#00E5FF" stopOpacity="0.7" />
            <stop offset="1" stopColor="#00E5FF" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[...Array(6)].map((_, i) => (
          <path
            key={i}
            d={`M 0 ${200 - i * 18} C 100 ${150 - i * 20}, 300 ${150 - i * 20}, 400 ${200 - i * 18}`}
            stroke="url(#gpuG)"
            strokeWidth="1"
            fill="none"
          />
        ))}
      </svg>
    );
  }
  if (kind === "industrial") {
    return (
      <svg
        viewBox="0 0 400 260"
        className="absolute right-0 bottom-0 w-full opacity-35"
      >
        <g stroke="#94A3B8" strokeOpacity="0.4" fill="none">
          {[...Array(20)].map((_, i) => (
            <line
              key={i}
              x1={0}
              y1={i * 14}
              x2={400}
              y2={i * 14 + 40}
              strokeWidth="0.6"
            />
          ))}
        </g>
      </svg>
    );
  }
  return null;
}
